import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class MetabaseService {
    private METABASE_SECRET_KEY = '3570286824fa5b667e86758e2df469db41d903e6e4d5de9b479774f7688ef028';

    /**
     * Generate Metabase JWT Token in Frontend (Browser) using Web Crypto API
     * @param dashboardId Metabase Dashboard ID
     * @param secretKey Metabase Secret Key
     */
    generateToken(
        dashboardId = 20,
        filterParams: Record<string, unknown> = {},
        embedding_params: Record<string, unknown> = {}
    ): Observable<string> {
        filterParams = this.cleanParams(filterParams);
        return from(this.createJwtToken(dashboardId, this.METABASE_SECRET_KEY, filterParams, embedding_params));
    }

    /**
     * Remove undefined and string "null" values from parameters
     */
    private cleanParams(params: Record<string, unknown>): Record<string, unknown> {
        const cleaned: Record<string, unknown> = {};
        Object.entries(params).forEach(([key, value]) => {
            if (value !== 'null' && value !== undefined) {
                cleaned[key] = value;
            }
        });
        return cleaned;
    }

    private async createJwtToken(
        dashboardId: number,
        secretKey: string,
        filterParams: Record<string, unknown>,
        embedding_params: Record<string, unknown>
    ): Promise<string> {
        const header = { alg: 'HS256', typ: 'JWT' };

        const cleanedParams = this.cleanParams(filterParams);

        const payload = {
            resource: { dashboard: dashboardId },
            params: cleanedParams,
            _embedding_params: embedding_params,
            exp: Math.floor(Date.now() / 1000) + 10 * 60
        };

        const base64UrlEncode = (data: Record<string, unknown>): string => {
            const str = JSON.stringify(data);
            const bytes = new TextEncoder().encode(str);
            let binString = '';
            bytes.forEach((b) => (binString += String.fromCharCode(b)));
            return btoa(binString).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
        };

        const encodedHeader = base64UrlEncode(header);
        const encodedPayload = base64UrlEncode(payload);
        const message = `${encodedHeader}.${encodedPayload}`;

        const encoder = new TextEncoder();
        const keyData = encoder.encode(secretKey);
        const messageData = encoder.encode(message);

        const cryptoKey = await window.crypto.subtle.importKey(
            'raw',
            keyData,
            { name: 'HMAC', hash: 'SHA-256' },
            false,
            ['sign']
        );

        const signatureBuffer = await window.crypto.subtle.sign('HMAC', cryptoKey, messageData);
        const signatureBytes = new Uint8Array(signatureBuffer);
        let signatureBin = '';
        signatureBytes.forEach((b) => (signatureBin += String.fromCharCode(b)));
        const encodedSignature = btoa(signatureBin).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');

        return `${message}.${encodedSignature}`;
    }
}
